"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader, Mail, RefreshCw, Shield, Play, AlertCircle } from "lucide-react";
import { useState, useEffect, useRef } from "react";
function VideoPlayer({
  mp4Url,
  posterUrl
}: {
  mp4Url: string;
  posterUrl: string;
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = 1.0;
    const handleError = () => {
      setHasError(true);
    };
    video.addEventListener('error', handleError);
    return () => {
      video.removeEventListener('error', handleError);
    };
  }, []);
  const handlePlayClick = () => {
    if (videoRef.current && !hasError) {
      videoRef.current.volume = 1.0;
      videoRef.current.play().catch(err => {
        console.error("Video play failed:", err);
        setHasError(true);
      });
      setIsPlaying(true);
      setHasStarted(true);
    }
  };
  const handleVideoEnd = () => {
    setIsPlaying(false);
  };
  const handleVideoPlay = () => {
    setIsPlaying(true);
    setHasStarted(true);
  };
  const handleVideoPause = () => {
    setIsPlaying(false);
  };
  if (hasError) {
    return <div className="relative w-full aspect-video rounded-lg overflow-hidden shadow-medium bg-surface border border-border flex items-center justify-center">
        <div className="text-center p-8">
          <AlertCircle className="size-12 text-destructive mx-auto mb-4" />
          <p className="text-foreground font-semibold mb-2">Video failed to load</p>
          <p className="text-muted-foreground text-sm">Please try refreshing the page</p>
        </div>
      </div>;
  }
  return <div className="relative w-full aspect-video rounded-lg overflow-hidden shadow-medium">
      <video ref={videoRef} className="w-full h-full object-cover" poster={posterUrl} controls={true} muted={false} onEnded={handleVideoEnd} onPlay={handleVideoPlay} onPause={handleVideoPause} playsInline preload="auto" webkit-playsinline="true" x-webkit-airplay="allow">
        <source src={mp4Url} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      
      {!hasStarted && <div className="absolute inset-0 flex items-center justify-center bg-black/20 cursor-pointer hover:bg-black/30 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)]" onClick={handlePlayClick}>
          <div className="bg-primary hover:bg-primary-hover active:bg-primary-active rounded-full p-6 shadow-strong hover:shadow-medium hover:-translate-y-1 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
            <Play className="size-12 text-foreground fill-current" />
          </div>
        </div>}
    </div>;
}
function SignUpForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  useEffect(() => {
    if (isSubmitted && !submitError) {
      setEmailVerificationSent(true);
      const checkEmailVerification = (event: any) => {
        if (event.detail?.verified) {
          setEmailVerified(true);
          setTimeout(() => {
            if (typeof window !== 'undefined' && (window as any).wvcClient) {
              (window as any).wvcClient.redirectToPhoneVerification?.();
            }
          }, 2000);
        }
      };
      if (typeof window !== 'undefined') {
        window.addEventListener('emailVerified', checkEmailVerification);
        return () => window.removeEventListener('emailVerified', checkEmailVerification);
      }
    }
  }, [isSubmitted, submitError]);
  const handleResendVerification = async () => {
    setIsResending(true);
    setResendMessage("");
    try {
      if (typeof window !== 'undefined' && (window as any).wvcClient) {
        await (window as any).wvcClient.resendEmailVerification?.();
        setResendMessage("Verification email has been resent. Please check your inbox.");
      }
    } catch (error) {
      setResendMessage("Failed to resend verification email. Please try again.");
    } finally {
      setIsResending(false);
    }
  };
  const handleChannelCreation = () => {
    if (typeof window !== 'undefined' && (window as any).wvcClient) {
      (window as any).wvcClient.triggerChannelCreation?.();
    }
  };
  const handleCreateChannel = () => {
    setEmailVerificationSent(false);
    setEmailVerified(false);
    setResendMessage("");
    resetForm();
    handleChannelCreation();
  };
  if (emailVerified) {
    return <div className="bg-surface border border-border rounded-lg p-8 max-w-md mx-auto text-center shadow-medium">
        <div className="mb-4 flex justify-center">
          <div className="bg-primary/10 rounded-full p-4">
            <Mail className="size-8 text-primary" />
          </div>
        </div>
        <p className="text-lg font-semibold text-primary mb-2">Email Verified!</p>
        <p className="text-muted-foreground mb-4">Redirecting you to phone verification...</p>
        <Loader className="animate-spin mx-auto size-6 text-primary" />
      </div>;
  }
  if (isSubmitted && !submitError && emailVerificationSent) {
    return <div className="bg-surface border border-border rounded-lg p-8 max-w-md mx-auto space-y-6 shadow-medium">
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            <div className="bg-primary/10 rounded-full p-4">
              <Mail className="size-8 text-primary" />
            </div>
          </div>
          <p className="text-lg font-semibold text-primary mb-2">{successMessage}</p>
          <p className="text-muted-foreground mb-4">
            We've sent a verification link to your email address. Please check your inbox and click the link to verify your account.
          </p>
          <p className="text-sm text-muted-foreground">
            After verifying your email, you'll be redirected to complete phone verification.
          </p>
        </div>

        {resendMessage && <div className={`p-4 rounded-lg border ${resendMessage.includes('Failed') ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            <p className={`text-sm ${resendMessage.includes('Failed') ? 'text-red-800' : 'text-green-800'}`}>
              {resendMessage}
            </p>
          </div>}

        <div className="flex flex-col gap-3">
          <Button onClick={handleResendVerification} disabled={isResending} variant="outline" className="w-full">
            {isResending ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Resending...
              </> : <>
                <RefreshCw className="mr-2 size-4" />
                Resend Verification Email
              </>}
          </Button>
          
          <Button onClick={handleCreateChannel} variant="ghost" className="w-full">
            Create Another Account
          </Button>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="bg-surface border border-border rounded-lg p-8 max-w-md mx-auto space-y-6 shadow-medium">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="bg-background border-border text-foreground" {...field} />
              </FormControl>
              <FormDescription className="text-muted-foreground">
                Enter your name as you'd like it displayed on your channel.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="bg-background border-border text-foreground" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="channel_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Desired Channel Name</FormLabel>
              <FormControl>
                <Input placeholder="MyAwesomeChannel" className="bg-background border-border text-foreground" {...field} />
              </FormControl>
              <FormDescription className="text-muted-foreground">
                Optional – the name of your future ClipVault channel.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="agree_terms" rules={{
        required: "You must accept the terms to create an account"
      }} render={({
        field
      }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-1" />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-foreground">
                  I agree to the Terms of Service and Privacy Policy
                </FormLabel>
                <FormDescription className="text-muted-foreground">
                  You must accept the terms to create an account.
                </FormDescription>
                <FormMessage />
              </div>
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-foreground-primary hover:bg-primary-hover active:bg-primary-active shadow-medium hover:shadow-strong hover:-translate-y-1 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Creating Your Channel...
            </> : "Create Your Channel"}
        </Button>
      </div>
    </Form>;
}
export default function Hero() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: subheadlineRef,
    isVisible: subheadlineVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: videoRef,
    isVisible: videoVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: policyRef,
    isVisible: policyVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section id="hero" className="relative flex min-h-[90vh] w-full items-start justify-center overflow-hidden bg-surface pt-32 pb-16">
      <div className="relative z-10 flex flex-col items-center justify-center px-4 text-center sm:px-6 lg:px-8 mt-8 max-w-7xl w-full">
        <h1 ref={headlineRef} className={`mb-4 font-display text-4xl font-bold text-foreground sm:text-5xl md:text-6xl lg:text-7xl transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>Share Your Videos With The World&nbsp;&nbsp;</h1>

        <p ref={subheadlineRef} className={`mb-8 max-w-2xl font-sans text-lg text-muted-foreground sm:text-xl md:text-2xl transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${subheadlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
        transitionDelay: "100ms"
      }}>
          Upload, stream, and monetize your video content for free with ViddTube's powerful video hosting and ad platform. Especially made for AI video creators. A true alternative to YouTube!
        </p>

        <div ref={videoRef} className={`mb-8 w-full max-w-4xl transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${videoVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
        transitionDelay: "150ms"
      }}>
          <VideoPlayer mp4Url="https://viddtube.com/wp-content/uploads/2026/02/ViddTube-AI-Video-Hosting-Platform-Youtube-Alternative-Solution-2-1.mp4" posterUrl="https://viddtube.com/wp-content/uploads/2026/02/https___viddtube.com_wp-content_uploads_2026_02_viddtube_preview_screenshot-1.png" />
        </div>

        <div ref={ctaRef} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
        transitionDelay: "200ms"
      }}>
          <FormProvider className="flex items-center justify-center" formId="cta_form_hero" sectionName="hero-section" defaultValues={{
          full_name: "",
          email: "",
          channel_name: "",
          agree_terms: false
        }} validationRules={{
          full_name: {
            required: true,
            minLength: 2
          },
          email: {
            required: true,
            pattern: "email"
          },
          agree_terms: {
            required: true
          }
        }} submitText="Create Your Channel" successMessage="Success! Please check your email to verify your account and complete setup." errorMessage="We couldn't process your request. Please verify your information and try again.">
            <SignUpForm />
          </FormProvider>
        </div>

        <div ref={policyRef} className={`mt-12 max-w-4xl w-full transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${policyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
        transitionDelay: "300ms"
      }}>
          <div className="bg-surface border border-border rounded-lg p-8 shadow-medium text-left">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-primary/10 rounded-full p-3">
                <Shield className="size-6 text-primary" />
              </div>
              <h2 className="font-display text-2xl font-bold text-foreground">
                Privacy Policy & Community Guidelines
              </h2>
            </div>

            <div className="space-y-6 text-foreground">
              <p className="text-base leading-relaxed">
                At ViddTube, we are committed to maintaining a safe, respectful, and inclusive platform for all users. By creating an account and uploading content, you agree to abide by our community guidelines and content policies.
              </p>

              <div>
                <h3 className="font-display text-lg font-semibold mb-3 text-primary">
                  Prohibited Content
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  The following types of content are strictly prohibited on ViddTube:
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>
                      <strong>Profanity and Offensive Language:</strong> Content containing excessive swearing, foul language, or derogatory terms that demean individuals or groups.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>
                      <strong>Nudity and Sexual Content:</strong> Videos containing explicit nudity, pornographic material, or sexually explicit content are not permitted.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>
                      <strong>Hate Speech:</strong> Content promoting hatred, discrimination, or violence against individuals or groups based on race, ethnicity, religion, gender, sexual orientation, disability, or other protected characteristics.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>
                      <strong>Violence and Graphic Content:</strong> Videos depicting gratuitous violence, gore, or content intended to shock or disturb viewers.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>
                      <strong>Harassment and Bullying:</strong> Content that targets, threatens, or harasses specific individuals or promotes cyberbullying.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>
                      <strong>Illegal Activities:</strong> Videos promoting or depicting illegal activities, including but not limited to drug use, weapons trafficking, or other criminal behavior.
                    </span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-display text-lg font-semibold mb-3 text-primary">
                  Content Moderation & Enforcement
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>
                      All uploaded content is subject to review by our automated systems and human moderators.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>
                      Violations may result in content removal, channel suspension, or permanent account termination.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>
                      Repeated violations will result in escalating penalties, including loss of monetization privileges.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>
                      Users can appeal content decisions through our support system.
                    </span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-display text-lg font-semibold mb-3 text-primary">
                  Your Responsibilities
                </h3>
                <p className="text-sm leading-relaxed">
                  As a content creator on ViddTube, you are responsible for ensuring that all uploaded content complies with our policies, respects intellectual property rights, and maintains appropriate age ratings. You must also respect viewer privacy and obtain necessary permissions for any individuals featured in your videos.
                </p>
              </div>

              <div className="pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  By using ViddTube, you acknowledge that you have read, understood, and agree to comply with these policies. ViddTube reserves the right to update these guidelines at any time. Continued use the platform constitutes acceptance of any changes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}